// src/app/core/chatPage/chatComponents/components/ModelSelector.tsx
import { useState, useRef, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { TextGenerationViewModel } from '@/app/core/chatPage/chatCopmonents/shared/TextGenerationViewModel';
import { AIModel } from '@/app/core/chatPage/chatCopmonents/shared/constants';
import { useTranslation } from '@/app/core/ui/theme/LanguageProvider';
import { CheckCircle, AlertTriangle, Zap, Sparkles, ChevronDown } from 'lucide-react';

interface ModelSelectorProps {
  onModelSelected: () => void;
}

export const ModelSelector = observer(({ onModelSelected }: ModelSelectorProps) => {
  const { t } = useTranslation();
  const viewModel = TextGenerationViewModel.getInstance();
  const [activeType, setActiveType] = useState<'text' | 'image' | 'video'>('text');
  const [expandedModelId, setExpandedModelId] = useState<string | null>(null);

  const types = [
    { key: 'text' as const, label: t('textModels') },
    { key: 'image' as const, label: t('imageModels') },
    { key: 'video' as const, label: t('videoModels') },
  ];

  // Свайп внутри карточки
  const touchStartX = useRef(0);
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    e.stopPropagation();
    touchStartX.current = e.changedTouches[0].screenX;
  }, []);
  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    e.stopPropagation();
    const delta = touchStartX.current - e.changedTouches[0].screenX;
    if (Math.abs(delta) < 40) return;
    if (delta > 0) {
      setActiveType(prev => {
        const idx = types.findIndex(t => t.key === prev);
        if (idx < types.length - 1) return types[idx + 1].key;
        return prev;
      });
    } else {
      setActiveType(prev => {
        const idx = types.findIndex(t => t.key === prev);
        if (idx > 0) return types[idx - 1].key;
        return prev;
      });
    }
  }, [types]);

  // Получаем модели для выбранного типа
  const getModelsForType = (type: 'text' | 'image' | 'video'): AIModel[] => {
  const target = type === 'text' ? 'chat' : type;
  return viewModel.availableModels.filter(model => model.channel === target);
};

  const models = getModelsForType(activeType);

  const getModelDetails = (model: AIModel) => {
    const text = `${model.name} ${model.description} ${model.provider || ''}`.toLowerCase();
    const strengths = [t('fastStart'), t('suitableForDaily')];
    const weaknesses = [t('qualityDependsOnPrompt')];

    if (text.includes("gpt")) {
      strengths.unshift(t('strongComplexInstructions'));
    } else if (text.includes("claude")) {
      strengths.unshift(t('holdsLongContext'));
    } else if (text.includes("gemini")) {
      strengths.unshift(t('universalBalance'));
    }

    return {
      strengths: strengths.slice(0, 2),
      weaknesses: weaknesses.slice(0, 2),
    };
  };

  return (
    <div className="px-3 py-3 overflow-y-auto h-full">
      {/* Карточка без общей подложки */}
      <div
        className="p-4 model-selector-area"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Точки (без изменений) */}
        <div className="flex justify-center gap-2 mb-4">
          {types.map(({ key }) => (
            <button
              key={key}
              onClick={(e) => {
                e.stopPropagation();
                setActiveType(key);
              }}
              className={`w-3 h-3 rounded-full transition-all border-2 ${
                activeType === key
                  ? 'bg-foreground border-foreground scale-110'
                  : 'bg-muted-foreground/25 border-muted-foreground/20 hover:bg-muted-foreground/40'
              }`}
              aria-label={t(`${key}Models`)}
            />
          ))}
        </div>

        {/* Заголовок категории */}
        <h3 className="text-lg font-bold text-foreground mb-3 text-center">
          {t(`${activeType}Models`)}
        </h3>

        {/* Список моделей */}
        <div className="space-y-2">
          {models.length === 0 ? (
            <div className="rounded-2xl bg-white/10 dark:bg-white/10 backdrop-blur-sm p-6 text-center">
              <p className="text-sm text-muted-foreground">
                {t('noModelsAvailable')}
              </p>
            </div>
          ) : (
            models.map((model) => {
              const isSelected = viewModel.selectedModelIdByType[activeType] === model.id;
              const isExpanded = expandedModelId === model.id;
              const details = getModelDetails(model);
              const shortDescription = viewModel.getTranslatedModelDescription(model.id, t as (key: string) => string).split('.')[0] + '.';

              return (
                <div
                  key={model.id}
                  className={`rounded-2xl border transition-all ${
                    isSelected
                      ? 'border-accent bg-accent/10 shadow-sm'
                      : 'border-border bg-background hover:bg-muted/20'
                  }`}
                >
                  <div
                    onClick={() => {
                      viewModel.setActiveModelType(activeType);
                      viewModel.setSelectedModel(model);
                      onModelSelected();
                    }}
                    className="w-full text-left p-3 flex items-center justify-between"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <Sparkles className="w-4 h-4 text-accent flex-shrink-0" />
                        <span className="text-sm font-semibold text-foreground truncate">{model.name}</span>
                      </div>
                      {!isExpanded && (
                        <p className="text-xs text-muted-foreground line-clamp-1">
                          {shortDescription}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                      {isSelected && <CheckCircle className="w-5 h-5 text-accent" />}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedModelId(isExpanded ? null : model.id);
                        }}
                        className="p-1 rounded-lg hover:bg-muted/30 transition-colors"
                      >
                        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                      </button>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="px-3 pb-3 border-t border-border/40 pt-2">
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <div className="flex items-center gap-1 text-xs font-medium text-green-600 dark:text-green-400">
                            <Zap className="w-3 h-3" />
                            <span>{t('strengths')}</span>
                          </div>
                          <ul className="space-y-0.5">
                            {details.strengths.map((s, idx) => (
                              <li key={idx} className="flex items-start gap-1 text-xs text-foreground/70">
                                <span className="w-1 h-1 rounded-full bg-green-500 mt-1.5 flex-shrink-0" />
                                {s}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="space-y-1">
                          <div className="flex items-center gap-1 text-xs font-medium text-rose-600 dark:text-rose-400">
                            <AlertTriangle className="w-3 h-3" />
                            <span>{t('weaknesses')}</span>
                          </div>
                          <ul className="space-y-0.5">
                            {details.weaknesses.map((w, idx) => (
                              <li key={idx} className="flex items-start gap-1 text-xs text-foreground/70">
                                <span className="w-1 h-1 rounded-full bg-rose-500 mt-1.5 flex-shrink-0" />
                                {w}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
});